// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineConnector,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from '@mui/lab';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, List, ListItemButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { ConfirmModal, Loading } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { PaymentOrderStatusEnum, RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { revertToQuote } from '~/thunks/order/orderThunk';
import { IOrderDetail } from '~/utils/interface/order';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
// Styles, images, icons
import styles from './TimeLineActionOrder.module.scss';
import { icons } from '~/assets';

type Props = {
  onMakePayment: () => void;
  onSendEmail: () => void;
  onPrintOrder: () => void;
  onPrintPickSlip: () => void;
  handleShowAllPaymentModal: () => void;
  detailOrder?: IOrderDetail;
};

const cx = classNames.bind(styles);

const TimeLineActionOrder = (props: Props) => {
  //#region Destructuring Props
  const { onMakePayment, onPrintOrder, onPrintPickSlip, handleShowAllPaymentModal, onSendEmail, detailOrder } = props;
  const { id: orderId } = useParams();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmRevertToQuote, setIsShowConfirmRevertToQuote] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowConfirmRevertToQuote = () => {
    setIsShowConfirmRevertToQuote(!isShowConfirmRevertToQuote);
  };

  const handleRevertToQuote = () => {
    if (!orderId) return;

    setIsLoading(true);

    dispatch(revertToQuote(orderId))
      .unwrap()
      .then((res) => {
        handleNavigateToQuote();
      })
      .catch(() => {})
      .finally(() => {
        handleShowConfirmRevertToQuote();
        setIsLoading(false);
      });
  };

  const handleNavigateToQuote = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.quotes);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.quotes);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.quotes);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.quotes);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.quotes);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='totalInfoQuoteDetailComponent' className={cx('container')}>
      <Timeline className={cx('timeline')}>
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color='primary' />
            <TimelineConnector />
          </TimelineSeparator>

          <TimelineOppositeContent className={cx('timelineOppositeContent')}>
            <Stack direction={'column'} spacing={4}>
              <Typography className={cx('title')}>
                {t('order_detail_action_timeline_title_order', { ns: 'admin/order' })}
              </Typography>
              <Button variant='contained' color='success' fullWidth onClick={handleShowConfirmRevertToQuote}>
                {t('order_detail_action_timeline_title_revert', { ns: 'admin/order' })}
              </Button>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconPaymentFee} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>
                  {t('quote_detail_action_timeline_title_payment_and_fees')}
                </Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                {detailOrder?.paymentStatus !== PaymentOrderStatusEnum.PAID && (
                  <ListItemButton className={cx('action')} onClick={onMakePayment}>
                    {t('quote_detail_action_button_collect_payment')}
                  </ListItemButton>
                )}

                <ListItemButton className={cx('action')} onClick={handleShowAllPaymentModal}>
                  {t('quote_detail_action_button_all_payment')}
                </ListItemButton>
              </List>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconEmail} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>{t('quote_detail_timeline_title_email')}</Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                <ListItemButton className={cx('action')} onClick={onSendEmail}>
                  {t('quote_detail_timeline_button_send_email')}
                </ListItemButton>
              </List>

              <Box className={cx('subTitleGroup')}>
                <img src={icons.commonIconPrint} alt={t('common_alt_img')} className={cx('iconSubtitle')} />

                <Typography className={cx('subTitle')}>{t('quote_detail_timeline_title_print')}</Typography>

                <Box className={cx('line')} />
              </Box>

              <List dense className={cx('listAction')}>
                <ListItemButton className={cx('action')} onClick={onPrintOrder}>
                  {t('quote_detail_timeline_button_print_order')}
                </ListItemButton>

                <ListItemButton className={cx('action')} onClick={onPrintPickSlip}>
                  {t('quote_detail_timeline_button_print_pick_slip')}
                </ListItemButton>
              </List>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>
      </Timeline>

      {isShowConfirmRevertToQuote && (
        <ConfirmModal
          type='default'
          icon={icons.menuIconQuotesUnactive}
          title={t('order_detail_revert_to_quote', { ns: 'admin/order' })}
          textConfirm={t('common_action_revert', { ns: 'common/base' })}
          onClose={handleShowConfirmRevertToQuote}
          onConfirm={handleRevertToQuote}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default TimeLineActionOrder;
